import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { AI4BA_BA_PROCESS_FLOWS } from '@/data/ai4baBaProcessFlows';
import BaProcessFlowSlide from './BaProcessFlowSlide';

interface BaProcessFlowCarouselProps {
  className?: string;
}

/** Auto-advance interval (ms). */
const AUTO_MS = 8000;

const BaProcessFlowCarousel = ({ className = '' }: BaProcessFlowCarouselProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = AI4BA_BA_PROCESS_FLOWS.length;
  const indexRef = useRef(0);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const goToIndex = useCallback(
    (next: number) => {
      if (total === 0) return;
      const wrapped = ((next % total) + total) % total;
      setIndex(wrapped);
      indexRef.current = wrapped;
    },
    [total]
  );

  const goNext = useCallback(() => {
    goToIndex(indexRef.current + 1);
  }, [goToIndex]);

  const goPrev = useCallback(() => {
    goToIndex(indexRef.current - 1);
  }, [goToIndex]);

  useEffect(() => {
    if (paused || total <= 1) return;
    const id = window.setInterval(() => {
      goNext();
    }, AUTO_MS);
    return () => window.clearInterval(id);
  }, [paused, total, goNext]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      const root = rootRef.current;
      if (!root) return;
      if (!root.contains(document.activeElement) && document.activeElement !== document.body) {
        if (!root.matches(':hover')) return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  const currentFlow = AI4BA_BA_PROCESS_FLOWS[index];

  return (
    <div
      ref={rootRef}
      data-ba-flow-carousel
      className={`rounded-xl border-2 border-amber-500/40 bg-gradient-to-b from-amber-500/10 via-m2-surface-container to-m2-surface-container overflow-hidden shadow-[0_0_0_1px_rgba(245,158,11,0.12)] ${className}`}
      tabIndex={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-3 border-b border-m2-outline-variant bg-m2-surface-low">
        <button
          type="button"
          onClick={goPrev}
          className="inline-flex items-center gap-1 rounded-md border border-m2-outline-variant px-2.5 py-1.5 text-sm font-medium text-m2-on-surface hover:bg-m2-surface-container"
          aria-label="Flow trước"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Trước</span>
        </button>
        <p className="text-sm text-m2-on-surface-variant text-center min-w-0 truncate px-2">
          <span className="font-semibold text-m2-on-surface">{currentFlow?.title}</span>
          <span className="mx-1.5 text-m2-outline">·</span>
          {index + 1}/{total}
          {!paused && (
            <span className="ml-2 text-xs text-m2-outline hidden sm:inline">tự chạy</span>
          )}
        </p>
        <button
          type="button"
          onClick={goNext}
          className="inline-flex items-center gap-1 rounded-md border border-m2-outline-variant px-2.5 py-1.5 text-sm font-medium text-m2-on-surface hover:bg-m2-surface-container"
          aria-label="Flow sau"
        >
          <span className="hidden sm:inline">Sau</span>
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Chỉ render slide hiện tại → chiều cao co giãn theo nội dung */}
      <div className="px-3 sm:px-5 py-4">
        {currentFlow && (
          <BaProcessFlowSlide
            key={currentFlow.id}
            flow={currentFlow}
            pauseAuto={paused}
            onJumpPhase={(phaseId) => {
              const i = AI4BA_BA_PROCESS_FLOWS.findIndex((f) => f.id === phaseId);
              if (i >= 0) goToIndex(i);
            }}
          />
        )}
      </div>

      <div className="flex justify-center gap-1.5 px-4 py-3 border-t border-m2-outline-variant">
        {AI4BA_BA_PROCESS_FLOWS.map((flow, i) => (
          <button
            key={flow.id}
            type="button"
            onClick={() => goToIndex(i)}
            aria-label={`Chuyển tới ${flow.title}`}
            aria-current={i === index ? 'true' : undefined}
            className={`h-2 rounded-full transition-all ${
              i === index ? 'w-6 bg-m2-primary' : 'w-2 bg-m2-outline-variant hover:bg-m2-outline'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default BaProcessFlowCarousel;
